import { CheckIcon, InfinityIcon, TicketIcon, WaitlistIcon, WriteIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, PurchaseItemStatus } from "common";
import { sumBy } from "lodash";
import React, { useState } from "react";
import { DigitalWaiver, Event, Experience, OPEN_UNLIMITED, Permission, ScheduleType, Timeslot } from "types";
import { EventApi } from "@fullcalendar/common";
import { IsGranted } from "../../Common/IsGranted";
import { ProductImage } from "../../Common/ProductImage";
import { EventNotesModal } from "../../Event/EventNotesModal";
import { AddonDisplay } from "../AddonDisplay";
import { BalanceBadge } from "../Common/BalanceBadge";
import { BasicInfo } from "../Common/BasicInfo";
import { NotesBadge } from "../Common/NotesBadge";
import { PendingBadgeCount } from "../PendingBadgeCount";
import { GuideDisplay } from "../Resources/Guides/GuideDisplay";
import { InventoryDisplay } from "../Resources/InventoryDisplay";
import {
    shouldShowBalance,
    shouldShowBalanceInFirstRow,
    shouldShowBalanceTooltip,
    shouldShowCheckedInAndVouchersSeparate,
    shouldShowExperienceInLastRow,
    shouldShowExperienceTitleInFirstRow,
    shouldShowName,
    shouldShowOnlyBalanceAndReservedCounterInMainInfo,
    shouldShowSubInfo,
    shouldShowSubInfoSeparate,
    shouldShowTime,
    shouldShowWaitlist,
} from "./dashboardResponsiveHelpers";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { useExperience } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCollection } from "@/hooks/useCollection";
import { isCheckInAllowed } from "@/models/Experience";
import { canOpenRoster, getConfirmedGuestCount, guestCountForReservedBookings } from "@/models/Timeslot";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";

enum DisplayType {
    FULL = "full",
    COMPACT = "compact",
}

// This file contains only responsive components that used in for all Calendar View types
interface DashboardCalendarItemBadgeContentProps {
    event: Event;
    experience?: Experience;
    timeslot: Timeslot;
    hasTimeslot: boolean;
    width: number;
    height: number;
}

//  DashboardCalendarItemBadgeContent show all timeslot info except Name and Time, this component used only for first timeslot row
export const DashboardCalendarItemBadgeContent = ({
    event,
    experience,
    timeslot,
    width,
    height,
    hasTimeslot,
}: DashboardCalendarItemBadgeContentProps) => {
    const {
        aggregatedBalance,
        statusCounts,
        capacity: { max, reserved },
    } = timeslot;

    const pendingCount =
        sumBy(statusCounts, (statusCount) =>
            statusCount.status === PurchaseItemStatus.PENDING_ACCEPT ||
            statusCount.status === PurchaseItemStatus.PENDING_MINIMUM
                ? statusCount.count
                : 0,
        ) ?? 0;

    return (
        <div className="h-full flex-col items-center justify-end space-x-1 space-y-1">
            <div className={clsx("flex h-fit items-center justify-end space-x-1 py-1")}>
                {!shouldShowSubInfoSeparate(height, width) && shouldShowSubInfo(height, width) && (
                    <SubInfoGrouped event={event} timeslot={timeslot} />
                )}

                <div className="flex flex-wrap-reverse items-center justify-end space-x-1">
                    {!shouldShowOnlyBalanceAndReservedCounterInMainInfo(height, width) &&
                        !shouldShowCheckedInAndVouchersSeparate(height, width) && (
                            <CheckedInAndVouchersGrouped timeslot={timeslot} experience={experience} />
                        )}

                    <div className="flex items-center space-x-1">
                        {shouldShowWaitlist(width) && (
                            <IsGranted permissions={Permission.WAITLISTS_VIEW}>
                                {timeslot.waitlistCount! > 0 ? (
                                    <BasicInfo
                                        text={`${timeslot.waitlistCount} Waitlist Count`}
                                        icon={<WaitlistIcon />}
                                        count={timeslot.waitlistCount!}
                                        className={clsx(
                                            "ui-dashboard-demographics items-center",
                                            hasTimeslot ? "font-bold" : "space-x-1 text-xs",
                                        )}
                                        testId="ui-dashboard-waitlist"
                                    />
                                ) : null}
                            </IsGranted>
                        )}

                        <strong className="text-xs" data-testid="dashboard-calendar-item-content-reserved">
                            {reserved}/{max === OPEN_UNLIMITED ? <InfinityIcon /> : max}
                        </strong>

                        {aggregatedBalance > 0 && (
                            <IsGranted permissions={Permission.AMOUNT_DUE_VIEW}>
                                {shouldShowBalanceInFirstRow(height) && shouldShowBalance(width) && (
                                    <BalanceBadge
                                        size="small"
                                        value={aggregatedBalance}
                                        showValueInTooltip={shouldShowBalanceTooltip(height, width)}
                                    />
                                )}
                            </IsGranted>
                        )}
                    </div>

                    {pendingCount > 0 && <PendingBadgeCount pendingCount={pendingCount} />}
                </div>
            </div>
        </div>
    );
};

interface CalendarItemTooltipHeadingProps {
    timeslot: Timeslot;
    title?: string;
    isMultiDayEvent?: boolean;
}

export const CalendarItemTooltipHeading = ({
    timeslot,
    title,
    isMultiDayEvent = false,
}: CalendarItemTooltipHeadingProps) => {
    const { arrivalDatetime, end, scheduleType } = timeslot;
    const timeFormat = isMultiDayEvent ? DateFormat.DAY_TIME : DateFormat.TIME;

    return (
        <>
            {scheduleType === ScheduleType.TIME && (
                <span>
                    {`${formatDate(arrivalDatetime, timeFormat)} - ${formatDate(end, timeFormat)}`} <br />
                </span>
            )}
            <span>{title}</span>
        </>
    );
};

interface DashboardCalendarItemTitleProps {
    fcEvent: EventApi;
    width: number;
    height: number;
    isMultiDayEvent?: boolean;
}

// DashboardCalendarItemTitle show only Name and Time of timeslot, this component used only for first row

export const DashboardCalendarItemTitle = ({
    fcEvent,
    width,
    height,
    isMultiDayEvent = false,
}: DashboardCalendarItemTitleProps) => {
    const {
        title: fcEventTitle,
        extendedProps: { timeslot, fcEventExperience },
    } = fcEvent;

    const { arrivalDatetime, scheduleType, product, unavailability }: Timeslot = timeslot;

    // Get the experience data in case it's undefined in fcEvent.
    const { experience: experienceData, isLoadingExperience } = useExperience(product, null, {
        enabled: !fcEventExperience || !fcEventTitle,
    });

    if (isLoadingExperience) {
        return null;
    }

    const experience = fcEventExperience ?? experienceData;

    // In cases when the experience is undefined, the title comes as "undefined" string
    const title = fcEventTitle && fcEventTitle !== "undefined" ? fcEventTitle : experience?.name;

    const displayTime = scheduleType === ScheduleType.TIME;

    const time = formatDate(arrivalDatetime, DateFormat.TIME).toUpperCase();

    return (
        <Tooltip
            content={
                <NoTranslate>
                    <CalendarItemTooltipHeading timeslot={timeslot} title={title} isMultiDayEvent={isMultiDayEvent} />
                    {unavailability?.message && (
                        <span className="block whitespace-pre-line">{unavailability.message}</span>
                    )}
                </NoTranslate>
            }
            placement="top"
            disabled={!canOpenRoster(timeslot)}
        >
            <div
                className={clsx("flex w-fit flex-shrink-0 flex-grow", {
                    "flex-col": shouldShowExperienceTitleInFirstRow(height, width),
                })}
            >
                <div className="flex items-center">
                    <ProductImage className="!mr-0.25" product={experience} size="tiny" />

                    {shouldShowTime(width) && (
                        <NoTranslate
                            if={displayTime}
                            className="text-xs"
                            data-testid="dashboard-calendar-item-content-arrival-time"
                        >
                            {displayTime ? time : "Time varies"}
                        </NoTranslate>
                    )}
                </div>

                {!shouldShowExperienceInLastRow(height) && shouldShowName(height, width) && (
                    <NoTranslate className="mx-1 w-full overflow-hidden text-ellipsis text-xs font-bold">
                        {title}
                    </NoTranslate>
                )}
            </div>
        </Tooltip>
    );
};

interface CheckedInAndVouchersGroupedProps {
    timeslot: Timeslot;
    experience?: Experience;
}

// CheckedInAndVouchersGrouped component shows timeslot counters: Checked in, Waivers, Vouchers. It used for first and second row in timeslot tile.
export const CheckedInAndVouchersGrouped = ({ timeslot, experience }: CheckedInAndVouchersGroupedProps) => {
    const { isGranted } = useAuthorization();
    const { data: waiverTemplates } = useCollection<DigitalWaiver>("waiverTemplates", null, {
        enabled: isGranted(Permission.WAIVERS_VIEW),
    });

    const {
        voucherCount = 0,
        waiverParticipantCount = 0, // The signed waivers
        otaPurchaseCount = 0,
        guestStatusCounts = [],
    } = timeslot;
    const seller = useSeller();
    const totalVoucherCount = voucherCount + otaPurchaseCount;
    const totalGuestCount = guestCountForReservedBookings(timeslot);
    const demographicCount = getConfirmedGuestCount(timeslot);
    const checkedInCount = sumBy(guestStatusCounts, (gc) => (gc.guestStatus === "arrived" ? gc.count : 0)) ?? 0;
    const isWaiverLinked = experience ? isProductHasLinkedWaiverTemplate(waiverTemplates, experience) : false;

    return (
        <div className="ml-2 flex flex-wrap gap-2">
            {isCheckInAllowed(seller, experience) && (
                <BasicInfo
                    text={
                        <>
                            <var data-var="checkedInCount">{checkedInCount}</var> checked-in guests
                        </>
                    }
                    icon={<CheckIcon />}
                    count={checkedInCount}
                    total={demographicCount}
                    className="ui-dashboard-demographics items-center text-xs"
                    testId="ui-dashboard-check-in"
                />
            )}
            {isWaiverLinked && (
                <BasicInfo
                    text="Waivers"
                    icon={<WriteIcon />}
                    count={waiverParticipantCount}
                    total={totalGuestCount}
                    className="ui-dashboard-waivers items-center text-xs"
                />
            )}
            <BasicInfo
                shouldAutoHide
                text="Vouchers"
                icon={<TicketIcon />}
                count={totalVoucherCount}
                className="ui-dashboard-vouchers items-center text-xs"
            />
        </div>
    );
};

interface SubInfoGroupedProps {
    timeslot: Timeslot;
    event: Event;
}

// SubInfoGrouped component shows timeslot info about Guides, Inventory, notes, addon. It used for first and second row in timeslot tile.

export const SubInfoGrouped = ({ timeslot, event }: SubInfoGroupedProps) => {
    const [showEventNotesModal, setShowEventNotesModal] = useState(false);
    const { noteCount } = timeslot;

    const toggleEventNotesModal = (e: MouseEvent) => {
        e.stopPropagation();
        setShowEventNotesModal(!showEventNotesModal);
    };

    return (
        <div className="flex w-fit flex-wrap items-center space-x-1">
            <GuideDisplay timeslot={timeslot} displayType={DisplayType.COMPACT} />
            <InventoryDisplay timeslot={timeslot} displayType={DisplayType.COMPACT} />

            {timeslot.purchasedUnits?.merchandise ? (
                <AddonDisplay purchasedAddon={timeslot.purchasedUnits?.merchandise} />
            ) : null}

            {noteCount > 0 && <NotesBadge noteCount={noteCount} onClick={toggleEventNotesModal} />}

            <EventNotesModal eventId={event?.id} isOpen={showEventNotesModal} onClose={toggleEventNotesModal} />
        </div>
    );
};
